import React, { useState, useEffect } from "react";
import { Modal, Button, Loader, Message } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../app/common/modals/modalSlice";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { fillAndFlattenPdf } from "../../../app/pdfLib/pdfLib";

export default function ComplianceDocumentPreviewModal({ document, transaction }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl;

    async function loadPdfUrl() {
      try {
        setLoading(true);

        if (document.fileUrl) {
          // Document already has a direct URL (e.g. an uploaded file)
          setPdfUrl(document.fileUrl);
        } else if (document.docRef) {
          if (document.status === "In Progress" || document.pdfBurnVersion) {
            // TransActioner-generated forms store an unfilled template at
            // docRef - fill in field values and signatures before viewing.
            const pdfBytes = await fillAndFlattenPdf(
              document,
              transaction,
              currentUserProfile
            );
            const blob = new Blob([pdfBytes], { type: "application/pdf" });
            objectUrl = URL.createObjectURL(blob);
            setPdfUrl(objectUrl);
          } else {
            const storage = getStorage();
            const storageRef = ref(storage, document.docRef);
            const url = await getDownloadURL(storageRef);
            setPdfUrl(url);
          }
        } else {
          setError("Document URL not found");
        }
      } catch (err) {
        console.error("Error loading document:", err);
        setError("Failed to load document");
      } finally {
        setLoading(false);
      }
    }

    if (document) {
      loadPdfUrl();
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [document, transaction, currentUserProfile]);

  function handleClose() {
    dispatch(closeModal({ modalType: "ComplianceDocumentPreviewModal" }));
  }

  return (
    <Modal open={true} onClose={handleClose} size="large">
      <Modal.Header>
        {document?.name || "Document Preview"}
      </Modal.Header>
      <Modal.Content scrolling style={{ minHeight: "600px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Loader active inline="centered">
              Loading document...
            </Loader>
          </div>
        )}
        
        {error && (
          <Message negative>
            <Message.Header>Error Loading Document</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        
        {!loading && !error && pdfUrl && (
          <div style={{ width: "100%", height: "600px" }}>
            <iframe
              src={`${pdfUrl}#toolbar=0`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title={document?.name || "Document Preview"}
            />
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Close</Button>
        {pdfUrl && (
          <Button
            primary
            as="a"
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in New Tab
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}

